import { GlobalState, setGeoLocation } from "app/slices/globalSlice";

import { closeModal, ModalState, openModal } from "app/slices/modalSlice";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modals } from "interfaces/enums";

// maximumAge accepts a recently cached fix — demanding a brand-new fix
// (maximumAge: 0) can hang for a long time on desktop machines even when
// permission is already granted.
export const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 20000,
  maximumAge: 5 * 60 * 1000,
};

export const GEO_STORAGE_KEY = "geolocation";

// Persisted last-known-good fix: only overwritten by a NEW successful fix,
// never cleared by errors — losing the signal must not re-prompt the user.
export const storeGeoLocation = (latitude: string, longitude: string) => {
  try {
    localStorage.setItem(
      GEO_STORAGE_KEY,
      JSON.stringify({ latitude, longitude }),
    );
  } catch {
    // Storage unavailable (private mode/quota) — non-fatal.
  }
};

const loadStoredGeoLocation = (): {
  latitude: string;
  longitude: string;
} | null => {
  try {
    const raw = localStorage.getItem(GEO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.latitude && parsed?.longitude) {
      return {
        latitude: String(parsed.latitude),
        longitude: String(parsed.longitude),
      };
    }
  } catch {
    // Corrupt entry — ignore.
  }
  return null;
};

export const GeoLocation = ({ children }: { children: any }) => {
  const [{ longitude, latitude, error, errorCode, loading }, setState] =
    useState(() => {
      const stored = loadStoredGeoLocation();
      return {
        longitude: stored?.longitude ?? "",
        latitude: stored?.latitude ?? "",
        error: "",
        errorCode: 0,
        loading: false,
      };
    });

  const { geolocation } = useSelector<any, GlobalState>(
    (state) => state.global,
  );
  const { modalName } = useSelector<any, ModalState>((state) => state.modal);
  // Read via ref inside the effect: modalName must NOT be a dep (the effect
  // would re-run — and re-dispatch — every time any modal opens or closes).
  const modalNameRef = useRef(modalName);
  modalNameRef.current = modalName;

  const dispatch = useDispatch();
  useEffect(() => {
    // Only close the modal when OURS is the one open — a blind closeModal()
    // dismisses whatever modal the user has open (e.g. Create Lead) every
    // time this effect re-runs after a background position refresh.
    const closeGeoModal = () => {
      if (modalNameRef.current === Modals.AcceptGeoLocation)
        dispatch(closeModal());
    };
    if (longitude && latitude) {
      closeGeoModal();
      dispatch(
        setGeoLocation({
          latitude: String(latitude),
          longitude: String(longitude),
        }),
      );
    } else if (geolocation.latitude && geolocation.longitude) {
      // Location was captured elsewhere (e.g. via the modal's
      // "Share My Location" button) — don't reopen the modal.
      closeGeoModal();
    } else {
      dispatch(
        openModal({
          modalName: Modals.AcceptGeoLocation,
          preventBackdrop: true,
          data: { error, errorCode, loading },
        }),
      );
    }
  }, [
    error,
    errorCode,
    latitude,
    longitude,
    loading,
    geolocation.latitude,
    geolocation.longitude,
    dispatch,
  ]);

  // Guards against React 18 StrictMode double-invoking this effect in dev —
  // a duplicate/cancelled request can strand the browser permission prompt.
  const startedRef = useRef(false);
  const watchIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!navigator.geolocation) {
      setState((s) => ({
        ...s,
        error: "Geolocation is not supported by this browser.",
        loading: false,
      }));
      return;
    }

    const onSuccess = (a: GeolocationPosition) => {
      storeGeoLocation(String(a.coords.latitude), String(a.coords.longitude));
      setState((s) => ({
        ...s,
        longitude: String(a.coords.longitude),
        latitude: String(a.coords.latitude),
        error: "",
        loading: false,
      }));
    };

    const onError = (e: GeolocationPositionError) => {
      setState((s) => {
        // Never let a transient failure wipe a position we already have.
        if (s.latitude && s.longitude) return s;
        return { ...s, error: e.message, errorCode: e.code, loading: false };
      });
    };

    // Once permission is granted there is no prompt involved, so a persistent
    // watch is safe — and it keeps retrying until the device can actually
    // resolve a position. This is what recovers WITHOUT a reload when the
    // user grants permission but the first fix isn't immediately available.
    const startWatch = () => {
      if (watchIdRef.current !== null) return;
      setState((s) => ({ ...s, loading: true }));
      watchIdRef.current = navigator.geolocation.watchPosition(
        onSuccess,
        onError,
        GEO_OPTIONS,
      );
    };

    // One-shot request — used to trigger the browser permission prompt when
    // permission hasn't been decided yet.
    const requestOnce = () => {
      setState((s) => ({ ...s, error: "", loading: true }));
      navigator.geolocation.getCurrentPosition(onSuccess, onError, GEO_OPTIONS);
    };

    // With a stored fix the app is already functional — refresh it silently
    // when permission is granted, but never fire anything that could show a
    // browser prompt or the modal again.
    const hasStored = Boolean(loadStoredGeoLocation());

    navigator.permissions
      ?.query({ name: "geolocation" })
      .then((status) => {
        if (status.state === "granted") {
          startWatch();
        } else if (status.state === "prompt" && !hasStored) {
          requestOnce();
        }
        // "denied": don't fire a request that instantly errors — the modal
        // explains how to re-enable access; onchange below picks up the flip.

        // The moment permission flips to granted (browser prompt, site
        // settings, or the modal button), start watching — Safari never
        // re-resolves the original pending request after a grant.
        status.onchange = () => {
          if (status.state === "granted") startWatch();
        };
      })
      .catch(() => {
        // Permissions API unavailable — fall back to a one-shot request; the
        // modal's "Share My Location" button covers recovery from there.
        // Skipped when a stored fix exists (the request could prompt).
        if (!hasStored) requestOnce();
      });

    // Intentionally no clearWatch cleanup: this component wraps the app for
    // its whole lifetime, and tearing down a watch mid-prompt (StrictMode
    // remount) cancels the in-flight permission request in Chrome.
  }, []);

  return children;
};
